import { useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Printer, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { BigButton } from "@/components/FormBits";
import { ScamNotice } from "@/components/PublicLayout";

export default function Confirmation({ result, office }) {
  const { t } = useLang();
  const [saved, setSaved] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(`MABUHAY CARE reference: ${result.reference}  PIN: ${result.pin}`); toast.success("Copied"); }
    catch { toast.error("Please write the numbers down instead."); }
  };
  return (
    <div data-testid="request-confirmation" className="fade-up">
      <h1 className="text-3xl sm:text-4xl font-black leading-tight">{t("received_title")}</h1>
      <p className="mt-4 text-base md:text-lg">{t("received_body")}</p>
      <div className="mt-8 rounded-2xl bg-white border-2 border-[#C59B27] p-6 sm:p-8 grid sm:grid-cols-2 gap-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#605349]">{t("reference")}</p>
          <p data-testid="confirmation-reference" className="mt-1 font-mono text-2xl sm:text-3xl font-bold text-[#4A2E1A] break-all">{result.reference}</p>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#605349] flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> Private PIN</p>
          <p data-testid="confirmation-pin" className="mt-1 font-mono text-2xl sm:text-3xl font-bold tracking-[0.3em] text-[#3D6346]">{result.pin}</p>
        </div>
        <p className="sm:col-span-2 text-[15px] text-[#5A4210] bg-[#FDF7E7] rounded-xl p-4">
          <b>Write these down or take a screenshot now.</b> The PIN is shown only once and cannot be recovered online. {office ? "Please give both numbers to the person." : "Keep it private — you need both to check your request."}
        </p>
      </div>
      <ul className="mt-6 space-y-2 text-[15px] text-[#605349] list-disc pl-5">
        <li>Submission does not guarantee immediate approval, a particular kind of assistance, or a specific response time.</li>
        <li>This service is not monitored continuously. In an emergency, call 911.</li>
        {result.safeguarding && <li data-testid="confirmation-safeguarding-note">Your request will be handled through a confidential pathway by specially designated personnel.</li>}
      </ul>
      {!office && <div className="mt-6"><ScamNotice compact /></div>}
      <div className="mt-8 flex flex-col sm:flex-row gap-3 no-print">
        <BigButton type="button" variant="ghost" data-testid="copy-reference-button" onClick={copy}><Copy className="w-5 h-5" /> Copy</BigButton>
        <BigButton type="button" variant="ghost" data-testid="print-reference-button" onClick={() => window.print()}><Printer className="w-5 h-5" /> Print</BigButton>
        <label className="flex items-center gap-2 text-[15px] px-2"><input type="checkbox" data-testid="confirm-saved-checkbox" className="w-5 h-5 accent-[#3D6346]" checked={saved} onChange={(e) => setSaved(e.target.checked)} /> I have saved my reference and PIN</label>
      </div>
      {saved && (
        <div className="mt-6 no-print">
          {office
            ? <Link to="/office/cases" data-testid="go-to-cases-link"><BigButton type="button">Go to cases</BigButton></Link>
            : <Link to="/" data-testid="back-home-link"><BigButton type="button">Back to home</BigButton></Link>}
        </div>
      )}
    </div>
  );
}
